<template>
  <section class="edit-personal-details">
    <Form
      ref="personalDetailsForm"
      autocomplete="off"
      autocorrect="off"
      autocapitalize="none"
      spellcheck="false"
      novalidate
      @submit="editPersonalDetails"
    >
      <base-input
        type="text"
        name="address"
        :model-value="personalDetails.address || ''"
        autocomplete="street-address"
        :validation-rules="validationSchema.address"
        :validation-errors="serverErrors.address"
        @update:model-value="$emit('change', { address: $event })"
      >
        <template #label>
          {{ $t('Address') }}
        </template>
        <template #label-status> &#8203; </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 256, stringLength: personalDetails?.address?.length }"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
        </template>
      </base-input>

      <base-input
        type="text"
        name="city"
        :model-value="personalDetails.city"
        :validation-rules="validationSchema.city"
        autocomplete="address-level2"
        @update:model-value="$emit('change', { city: $event })"
      >
        <template #label>
          {{ $t('City') }}
        </template>
        <template #label-status> &#8203; </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 256, stringLength: personalDetails?.city?.length }"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
        </template>
      </base-input>

      <base-input
        type="text"
        name="county"
        :model-value="personalDetails.county"
        :validation-rules="validationSchema.county"
        autocomplete="address-level3"
        @update:model-value="$emit('change', { county: $event })"
      >
        <template #label>
          {{ $t('County') }}
        </template>
        <template #label-status> &#8203; </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 256, stringLength: personalDetails?.county?.length }"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
        </template>
      </base-input>

      <base-input
        type="text"
        name="postcode"
        :model-value="personalDetails.postcode"
        :validation-rules="validationSchema.postcode"
        autocomplete="postal-code"
        @update:model-value="$emit('change', { postcode: $event })"
      >
        <template #label>
          {{ $t('Postcode') }}
        </template>
        <template #label-status> &#8203; </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 10, stringLength: personalDetails?.postcode?.length }"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
        </template>
      </base-input>

      <base-country
        :model-value="personalDetails.country"
        name="country"
        autocomplete="country"
        @update:modelValue="changeCountry"
      >
        <template #label>
          {{ $t('Country') }}
        </template>
        <template #label-status> &#8203; </template>
      </base-country>

      <base-phone
        :model-value="personalDetails.telephone"
        name="telephone"
        :required="false"
        @update:model-value="changeTelephone"
      >
        <template #label>
          {{ $t('Telephone') }}
        </template>
        <template #errorMessage>
          <span v-if="serverErrors.telephone">{{
            serverErrors.telephone[0]
          }}</span>
        </template>
      </base-phone>

      <base-select
        :select-options="unitTypes"
        name="type_of_unit"
        class="type-of-unit"
        @change="$emit('change', { unitType: { id: $event.selectedValue } })"
      >
        <template #label>
          <translate>Type of unit</translate>
        </template>
        <template #errorMessage>
          <translate>
            Please select the type of unit you are planning to bring
          </translate>
        </template>
        <template #label-status> &#8203; </template>
      </base-select>

      <base-input
        v-model="dimensionsWidth"
        v-format-number="1"
        :placeholder="$t('metres')"
        autocomplete="off"
        name="dimensions_width"
        type="text"
        class="unit"
        :validation-rules="validationSchema.dimensions_width"
        inputmode="decimal"
      >
        <template #label>
          <translate>Width (metres)</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate v-if="failedRules.includes('min_value')">
            Please enter a width
          </translate>
          <translate v-else-if="failedRules.includes('number')">
            Please enter a number
          </translate>
          <span v-else>{{ failedRules[0] }}</span>
        </template>
        <template #label-status> &#8203; </template>
      </base-input>

      <base-input
        v-model="dimensionsDepth"
        v-format-number="1"
        :placeholder="$t('metres')"
        autocomplete="off"
        name="dimensions_depth"
        type="text"
        class="unit"
        :validation-rules="validationSchema.dimensions_depth"
        inputmode="decimal"
      >
        <template #label>
          <translate>Length (metres)</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate v-if="failedRules.includes('required')">
            Please tell us the length of your unit
          </translate>
          <translate v-else-if="failedRules.includes('min_value')">
            Please enter a length
          </translate>
          <translate v-else-if="failedRules.includes('number')">
            Please enter a number
          </translate>
          <span v-else>{{ failedRules[0] }}</span>
        </template>
        <template #label-status> &#8203; </template>
      </base-input>

      <base-input
        type="text"
        name="car_registration_number"
        class="car-registration"
        :model-value="personalDetails.carRegistrationNumber"
        :validation-rules="validationSchema.carRegistrationNumber"
        @update:model-value="$emit('change', { carRegistrationNumber: $event })"
      >
        <template #label>
          {{ $t('Vehicle registration number') }}
        </template>
        <template #label-status> &#8203; </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{
              max: 55,
              stringLength: personalDetails?.carRegistrationNumber?.length,
            }"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
        </template>
      </base-input>

      <base-textarea
        :model-value="personalDetails.namesOfAllPartyMembers"
        name="names_of_all_party_members"
        rows="4"
        @update:model-value="
          $emit('change', { namesOfAllPartyMembers: $event })
        "
      >
        <template #label>
          {{ $t('Names of all party members') }}
        </template>
        <template #label-status> &#8203; </template>
      </base-textarea>

      <!-- <base-select
        name="gender_options"
        :select-options="genderOptions"
        @change="$emit('change', { gender: $event.selectedValue })"
      >
        <template #label>
          <translate>Gender</translate>
        </template>
        <template #label-status> &#8203; </template>
      </base-select> -->

      <div class="row action-buttons">
        <a class="button minimal cancel" @click.prevent="$emit('cancel')">{{
          $t('Cancel')
        }}</a>
        <button class="primary asblock">
          {{ $t('Save') }}
        </button>
      </div>
    </Form>
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type { UserPersonalDetails } from '../types'
import { Form } from 'vee-validate'
import * as z from 'zod'

@Component({
  emits: ['change', 'submit', 'cancel'],
  components: { Form },
})
class PersonalDetailsForm extends Vue {
  hasUserChangedTelephone = false

  @Prop()
  personalDetails: UserPersonalDetails

  @Prop()
  serverErrors: Record<string, string[]>

  get dimensionsWidth() {
    return this.personalDetails.dimensionsWidth
  }

  set dimensionsWidth(width) {
    this.$emit('change', { dimensionsWidth: width })
  }

  get dimensionsDepth() {
    return this.personalDetails.dimensionsDepth
  }

  set dimensionsDepth(depth) {
    this.$emit('change', { dimensionsDepth: depth })
  }

  editPersonalDetails() {
    this.$emit('submit')
  }

  validationSchema = {
    dimensions_width: z
      .string()
      .refine((v) => !isNaN(v), { message: 'number' })
      .optional()
      .nullable(),
    dimensions_depth: z
      .string()
      .refine((v) => !isNaN(v), { message: 'number' })
      .optional()
      .nullable(),
    address: z.string().max(256, { message: 'max' }).optional().nullable(),
    city: z.string().max(256, { message: 'max' }).optional().nullable(),
    county: z.string().max(256, { message: 'max' }).optional().nullable(),
    postcode: z.string().max(10, { message: 'max' }).optional().nullable(),
    carRegistrationNumber: z
      .string()
      .max(55, { message: 'max' })
      .optional()
      .nullable(),
  }

  changeCountry(event: any) {
    const telephone = this.personalDetails.telephone
    if (
      (telephone && telephone.countryDialingCode === '') ||
      !this.hasUserChangedTelephone
    ) {
      this.$emit('change', {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Event from base-country component is untyped
        telephone: { ...telephone, countryDialingCode: event },
      })
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Event from base-country component is untyped
    this.$emit('change', { country: event })
  }

  changeTelephone(event: any) {
    this.hasUserChangedTelephone = true
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Event from base-phone component is untyped
    this.$emit('change', { telephone: event })
  }

  get genderOptions() {
    return [
      {
        value: '',
        name: ' ',
        selected: !this.personalDetails.gender,
      },
      {
        value: 'male',
        name: this.$t('Male'),
        selected: this.personalDetails.gender === 'male',
      },
      {
        value: 'female',
        name: this.$t('Female'),
        selected: this.personalDetails.gender === 'female',
      },
    ]
  }

  get unitTypes() {
    const unitTypes = [
      { id: '14', name: this.$t('Tent') },
      { id: '15', name: this.$t('Trailer tent') },
      { id: '16', name: this.$t('Motorhome') },
      { id: '17', name: this.$t('Campervan') },
      { id: '18', name: this.$t('Touring caravan') },
    ]

    return [
      {
        value: '',
        name: '---',
        selected: !this.personalDetails.unitType,
      },
      ...unitTypes.map((subCat) => {
        return {
          value: subCat.id,
          name: subCat.name,
          selected: this.personalDetails.unitType?.id === subCat.id,
        }
      }),
    ]
  }
}

export default toNative(PersonalDetailsForm)
</script>

<style scoped>
.edit-personal-details {
  padding: 0.6em 0;
}

.edit-personal-details :deep(label) {
  color: var(--colour-dark-blue);
  margin: 0 0 1em 0;
  display: block;
}

.row {
  align-items: center;
  padding: 1em 0 1em;
  border-top: 1px solid var(--colour-page-background);
  margin-top: 2em;
  gap: 2em;
}

.cancel {
  margin-top: 0.25em;
}

.car-registration :deep(input),
.unit :deep(input) {
  max-width: 11em;
}

.type-of-unit :deep(.select select) {
  font-size: 1.24em;
}

:deep(.select select) {
  font-size: 1.25em;
}

.action-buttons {
  justify-content: space-between;
}

.action-buttons button.primary {
  max-width: 20em;
}
</style>
